import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {NgOptimizedImage} from "@angular/common";
import {TranslatePipe} from "@ngx-translate/core";
import {CardComponent} from "../../../libs/card/card.component";
import {CardBodyComponent} from "../../../libs/card/card-body/card-body.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {SettingsService} from "../../../services/settings.service";
import {SettingKey} from "../../../base/setting-key.enum";
import {Subscription} from "rxjs";
import {unsubscribe} from "../../../libs/utils/epic.utils";
import {SettingDto} from "../../../repositories/dto/setting.dto";
import {PageHeaderComponent} from "../../../libs/header/page-header/page-header.component";
import {NavBarComponent} from "../../../libs/nav-bar/nav-bar.component";
import {NavBarItem} from "../../../libs/nav-bar/nav-bar.item";
import {UserService} from "../../../services/user.service";
import {UserDto} from "../../../repositories/dto/user.dto";
import {Paths} from "../../../paths";
import {Router} from "@angular/router";

@Component({
  selector: 'acc-layout',
  imports: [
    NgOptimizedImage,
    TranslatePipe,
    CardComponent,
    CardBodyComponent,
    PageHeaderComponent,
    NavBarComponent,
    NgbTooltip
  ],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './layout.component.css'
})
export class LayoutComponent implements OnInit, OnDestroy {

  public static readonly MODE_AUTO = 'auto';
  public static readonly MODE_DARK = 'dark';
  public static readonly MODE_LIGHT = 'light';
  public static readonly MODE_ALL = ['auto', 'dark', 'light'];

  public static readonly COLOR_PREFIX = 'theme-color-';
  public static readonly COLOR_DEFAULT = 'default';
  public static readonly COLOR_ALL = ['default', 'blue', 'gray', 'red', 'yellow', 'pink'];

  public static readonly SIDEBAR_PREFIX = 'sidebar-';
  public static readonly SIDEBAR_DEFAULT = 'default';
  public static readonly SIDEBAR_ALL = ['default', 'dark', 'color', 'transparent'];

  public static readonly DIRECTION_DEFAULT = 'ltr';

  private readonly subs: Subscription[] = [];
  private readonly settingKeys: string[] = [
    SettingKey[SettingKey.USER_LAYOUT_MODE],
    SettingKey[SettingKey.USER_LAYOUT_COLOR],
    SettingKey[SettingKey.USER_LAYOUT_SIDEBAR],
    SettingKey[SettingKey.USER_LAYOUT_DIRECTION]
  ];

  protected title: string = '';
  protected mode: string = LayoutComponent.MODE_AUTO;
  protected color: string = LayoutComponent.COLOR_DEFAULT;
  protected sidebar: string = LayoutComponent.SIDEBAR_DEFAULT;
  protected direction: string = LayoutComponent.DIRECTION_DEFAULT;
  protected navItemsSelected = new NavBarItem('layout');
  protected navItems: NavBarItem[] = [
    new NavBarItem('profile'),
    this.navItemsSelected
  ];

  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly userService: UserService = inject(UserService);
  private readonly router: Router = inject(Router);

  ngOnInit(): void {
    this.subs.push(
      this.settingsService.getUserSettings().subscribe((s) => this.setSettings(s)),
      this.userService.getUser().subscribe((user) => this.setUser(user)));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected updateMode(mode: string | undefined): void {
    if (mode) {
      this.setMode(mode);
      this.settingsService.updateUserSetting(SettingKey.USER_LAYOUT_MODE, mode).then();
    }
  }

  protected updateColor(color: string | undefined): void {
    if (color) {
      this.setColor(color);
      this.settingsService.updateUserSetting(SettingKey.USER_LAYOUT_COLOR, color).then();
    }
  }

  protected updateSidebar(sidebar: string | undefined): void {
    if (sidebar) {
      this.setSidebar(sidebar);
      this.settingsService.updateUserSetting(SettingKey.USER_LAYOUT_SIDEBAR, sidebar).then();
    }
  }

  protected updateDirection(direction: string | undefined): void {
    if (direction) {
      this.setDirection(direction);
      this.settingsService.updateUserSetting(SettingKey.USER_LAYOUT_DIRECTION, direction).then();
    }
  }

  protected selectNav(item: NavBarItem): void {
    if (item.name === 'profile') {
      this.router.navigate([Paths.USER_PROFILE]).then();
    }
  }

  private setMode(mode: string | undefined): void {
    if (mode) {
      this.mode = mode;
    }
  }

  private setColor(color: string | undefined): void {
    if (color) {
      this.color = color;
    }
  }

  private setSidebar(sidebar: string | undefined): void {
    if (sidebar) {
      this.sidebar = sidebar;
    }
  }

  private setDirection(direction: string | undefined): void {
    if (direction) {
      this.direction = direction;
    }
  }

  private setSettings(settings: SettingDto[]): void {
    settings
      .filter((s) => this.settingKeys.includes(s.key))
      .forEach((s) => {
        switch (s.key) {
          case SettingKey[SettingKey.USER_LAYOUT_MODE]:
            this.setMode(s.value);
            break;
          case SettingKey[SettingKey.USER_LAYOUT_COLOR]:
            this.setColor(s.value);
            break;
          case SettingKey[SettingKey.USER_LAYOUT_SIDEBAR]:
            this.setSidebar(s.value);
            break;
          case SettingKey[SettingKey.USER_LAYOUT_DIRECTION]:
            this.setDirection(s.value);
            break;
        }
      });
  }

  private setUser(user: UserDto): void {
    this.title = user.getFullName();
  }
}
